import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Centralised front-end security helpers.
 *
 * Added as part of the OWASP / VAPT hardening pass (see SECURITY_AUDIT_REPORT.md).
 * Keep this dependency-free so it can be reused by any component / guard.
 */

/** Enterprise password policy: >=12 chars, upper, lower, digit, special. */
export const PASSWORD_POLICY = {
    minLength: 12,
    // At least one lowercase, one uppercase, one digit, one special character.
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/,
};

/**
 * A small block-list of well-known weak passwords that must always be rejected,
 * independent of whether they technically satisfy the complexity rules.
 */
export const WEAK_PASSWORDS: ReadonlySet<string> = new Set([
    '123456', '123456789', 'password', 'password123', 'admin', 'admin123',
    'qwerty', 'qwerty123', 'welcome', 'welcome123', 'letmein', 'iloveyou',
    'abc123', 'changeme', 'passw0rd', '111111', '000000', 'dreamstep',
    'vtrackon@123', 'dreamstep@123',
]);

/** Returns true when the supplied value satisfies the full password policy. */
export function isStrongPassword(value: string | null | undefined): boolean {
    if (!value) { return false; }
    if (WEAK_PASSWORDS.has(value.toLowerCase())) { return false; }
    return PASSWORD_POLICY.pattern.test(value);
}

/**
 * Angular reactive-forms validator enforcing the enterprise password policy.
 * Usage:  new FormControl('', [passwordStrengthValidator()])
 */
export function passwordStrengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value: string = control.value;
        if (!value) { return null; } // let `required` handle empty
        const errors: ValidationErrors = {};
        if (value.length < PASSWORD_POLICY.minLength) { errors['minlength'] = true; }
        if (!/[a-z]/.test(value)) { errors['lowercase'] = true; }
        if (!/[A-Z]/.test(value)) { errors['uppercase'] = true; }
        if (!/\d/.test(value)) { errors['number'] = true; }
        if (!/[^A-Za-z0-9]/.test(value)) { errors['special'] = true; }
        if (WEAK_PASSWORDS.has(value.toLowerCase())) { errors['weak'] = true; }
        return Object.keys(errors).length ? { passwordStrength: errors } : null;
    };
}

/**
 * Escapes a string for safe insertion into innerHTML. Use this whenever raw
 * (server- or user-supplied) text must be concatenated into an HTML template
 * literal instead of bound through Angular's auto-escaping interpolation.
 */
export function escapeHtml(input: unknown): string {
    if (input === null || input === undefined) { return ''; }
    return String(input)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Open-redirect guard. Only allows redirecting to same-origin internal paths
 * (e.g. "/internal/dashboard"). Rejects absolute URLs, protocol-relative URLs
 * and anything pointing at another host.
 */
export function isSafeInternalRedirect(url: string | null | undefined): boolean {
    if (!url) { return false; }
    // Reject absolute (http:, https:, javascript:, data:) and protocol-relative ("//host").
    if (/^[a-z][a-z0-9+.-]*:/i.test(url)) { return false; }
    if (url.startsWith('//')) { return false; }
    // Must be a rooted, in-app path.
    return url.startsWith('/');
}
